/**
 * FirstTimeUserPrompt
 * Manages the first-time user login prompt popup that appears when a user
 * asks their very first question, encouraging them to sign in for benefits
 */
export class FirstTimeUserPrompt {
    constructor() {
        this.hasShownPromptThisSession = false; // Track if shown this session
        this.isUserAuthenticated = false;
        
        // Initialize the prompt modal
        this.createPromptModal();
        this.setupEventListeners();
        
        // Set up authentication monitoring
        this.setupAuthenticationMonitoring();
    }

    /**
     * Set up authentication monitoring that works with existing Firebase auth system
     */
    setupAuthenticationMonitoring() {
        // Wait for Firebase to initialize, then set up proper auth monitoring
        const waitForFirebase = () => {
            if (window.firebaseAuth && window.firebaseModules) {
                console.log('FirstTimeUserPrompt: Firebase detected, setting up auth monitoring');
                
                // Set up Firebase auth state listener
                window.firebaseModules.onAuthStateChanged(window.firebaseAuth, (user) => {
                    const wasAuthenticated = this.isUserAuthenticated;
                    this.isUserAuthenticated = !!user;
                    
                    if (wasAuthenticated !== this.isUserAuthenticated) {
                        if (this.isUserAuthenticated) {
                            console.log('FirstTimeUserPrompt: User logged in via Firebase - popup disabled');
                            // If popup is currently showing, hide it
                            const modal = document.getElementById('first-time-prompt-modal');
                            if (modal && modal.style.display === 'flex') {
                                this.closePrompt();
                            }
                        } else {
                            console.log('FirstTimeUserPrompt: User logged out - popup enabled for new session');
                            // Reset session status when user logs out
                            this.hasShownPromptThisSession = false;
                        }
                    }
                });
                
                // Check current auth state immediately
                const currentUser = window.firebaseAuth.currentUser;
                this.isUserAuthenticated = !!currentUser;
                console.log('FirstTimeUserPrompt: Initial auth state -', this.isUserAuthenticated ? 'authenticated' : 'not authenticated');
                
            } else {
                // Firebase not ready yet, check again in 500ms
                setTimeout(waitForFirebase, 500);
            }
        };
        
        // Start waiting for Firebase
        waitForFirebase();
        
        // Also listen for custom auth events that might be dispatched
        document.addEventListener('userAuthenticated', () => {
            this.isUserAuthenticated = true;
            console.log('FirstTimeUserPrompt: Custom auth event - user authenticated');
        });
        
        document.addEventListener('userSignedOut', () => {
            this.isUserAuthenticated = false;
            this.hasShownPromptThisSession = false; // Reset for new session
            console.log('FirstTimeUserPrompt: Custom auth event - user signed out');
        });
    }

    /**
     * Create the first-time user prompt modal HTML
     */
    createPromptModal() {
        const modalHTML = `
            <div id="first-time-prompt-modal" class="first-time-prompt-modal" style="display: none;">
                <div class="first-time-modal-content">
                    <div class="first-time-close btn rounded-circle d-flex align-items-center justify-content-center" 
                         id="close-first-time-prompt">
                        <i class="fas fa-times"></i>
                    </div>
                    <div class="first-time-container">
                        <div class="first-time-header">
                            <div class="welcome-icon">
                                <i class="fas fa-star"></i>
                            </div>
                            <h2>Welcome to Legend Lab!</h2>
                            <p>Get the most out of your experience</p>
                        </div>
                        
                        <div class="benefits-list">
                            <div class="benefit-item">
                                <i class="fas fa-history"></i>
                                <span>Log in to save your chat history</span>
                            </div>
                            <div class="benefit-item">
                                <i class="fas fa-users"></i>
                                <span>Access exclusive characters</span>
                            </div>
                            <div class="benefit-item">
                                <i class="fas fa-sync-alt"></i>
                                <span>Continue conversations across devices</span>
                            </div>
                        </div>
                        
                        <div class="first-time-actions">
                            <button class="btn btn-golden btn-lg w-100 mb-3" onclick="firstTimeSignIn()">
                                <i class="fas fa-sign-in-alt me-2"></i>Sign In Now
                            </button>
                            <button class="btn btn-outline-light w-100" id="first-time-continue-btn">
                                Continue Without Signing In
                            </button>
                        </div>
                        
                        <div class="first-time-footer">
                            <small class="text-muted">You can always sign in later from the settings menu</small>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add the modal to the document body
        document.body.insertAdjacentHTML('beforeend', modalHTML);
        
        // Add the CSS styles
        this.addPromptStyles();
        
        // Add direct event listener to close button for immediate functionality
        const closeButton = document.getElementById('close-first-time-prompt');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('FirstTimeUserPrompt: Direct close button clicked');
                this.closePrompt();
            });
        }
    }

    /**
     * Add CSS styles for the first-time prompt modal
     */
    addPromptStyles() {
        const styles = `
            <style>
                .first-time-prompt-modal {
                    position: fixed;
                    z-index: 15000;
                    left: 0;
                    top: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(184, 149, 45, 0.15);
                    backdrop-filter: blur(15px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    animation: firstTimeModalFadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                }

                @keyframes firstTimeModalFadeIn {
                    from {
                        opacity: 0;
                        backdrop-filter: blur(0px);
                    }
                    to {
                        opacity: 1;
                        backdrop-filter: blur(15px);
                    }
                }

                .first-time-modal-content {
                    background: linear-gradient(145deg, 
                        rgba(255, 255, 255, 0.45) 0%, 
                        rgba(255, 255, 255, 0.25) 50%, 
                        rgba(255, 255, 255, 0.15) 100%);
                    border-radius: 32px;
                    box-shadow: 
                        0 50px 100px rgba(0, 0, 0, 0.4),
                        0 0 0 1px rgba(255, 255, 255, 0.1),
                        inset 0 1px 0 rgba(255, 255, 255, 0.15),
                        0 25px 70px rgba(184, 149, 45, 0.15);
                    width: 90%;
                    max-width: 520px;
                    position: relative;
                    overflow: hidden;
                    transform: scale(0.8) translateY(40px);
                    animation: firstTimeSlideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
                    backdrop-filter: blur(25px);
                }

                @keyframes firstTimeSlideIn {
                    from {
                        transform: scale(0.8) translateY(40px);
                        opacity: 0;
                    }
                    to {
                        transform: scale(1) translateY(0);
                        opacity: 1;
                    }
                }

                @keyframes firstTimeSlideOut {
                    from {
                        transform: scale(1) translateY(0);
                        opacity: 1;
                    }
                    to {
                        transform: scale(0.8) translateY(-40px);
                        opacity: 0;
                    }
                }

                .first-time-modal-content::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, 
                        transparent 0%, 
                        rgba(184, 149, 45, 0.7) 20%, 
                        rgba(184, 149, 45, 1) 50%, 
                        rgba(184, 149, 45, 0.7) 80%, 
                        transparent 100%);
                    border-radius: 32px 32px 0 0;
                    animation: firstTimeBorderShimmer 4s ease-in-out infinite;
                }

                @keyframes firstTimeBorderShimmer {
                    0%, 100% { opacity: 0.7; }
                    50% { opacity: 1; }
                }

                .first-time-close {
                    position: absolute;
                    right: 24px;
                    top: 16px;
                    width: 44px;
                    height: 44px;
                    background-color: rgba(184, 149, 45, 0.6);
                    border: 2px solid rgba(184, 149, 45, 0.8);
                    cursor: pointer;
                    box-shadow: 0 3px 10px rgba(0,0,0,0.4);
                    z-index: 10;
                    transition: all 0.3s ease;
                }

                .first-time-close i {
                    font-size: 22px;
                    color: white;
                }

                .first-time-close:hover {
                    background-color: rgba(184, 149, 45, 0.8);
                    border-color: rgba(184, 149, 45, 1);
                    transform: scale(1.1);
                    box-shadow: 0 5px 15px rgba(0,0,0,0.5);
                }

                .first-time-container {
                    padding: 60px 52px 52px;
                }

                .first-time-header {
                    text-align: center;
                    margin-bottom: 48px;
                }

                .welcome-icon {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, rgba(184, 149, 45, 0.8), rgba(184, 149, 45, 0.6));
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 24px;
                    box-shadow: 0 8px 25px rgba(184, 149, 45, 0.3);
                    animation: welcomeIconPulse 3s ease-in-out infinite;
                }

                @keyframes welcomeIconPulse {
                    0%, 100% { transform: scale(1); box-shadow: 0 8px 25px rgba(184, 149, 45, 0.3); }
                    50% { transform: scale(1.05); box-shadow: 0 12px 35px rgba(184, 149, 45, 0.4); }
                }

                .welcome-icon i {
                    font-size: 36px;
                    color: white;
                }

                .first-time-header h2 {
                    color: white;
                    font-size: 2.2rem;
                    font-weight: 700;
                    margin-bottom: 12px;
                    text-shadow: 0 2px 8px rgba(0,0,0,0.3);
                }

                .first-time-header p {
                    color: rgba(255, 255, 255, 0.85);
                    font-size: 1.1rem;
                    margin: 0;
                }

                .benefits-list {
                    margin-bottom: 40px;
                }

                .benefit-item {
                    display: flex;
                    align-items: center;
                    padding: 16px 0;
                    color: white;
                    font-size: 1.05rem;
                    font-weight: 500;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }

                .benefit-item:last-child {
                    border-bottom: none;
                }

                .benefit-item i {
                    width: 24px;
                    height: 24px;
                    margin-right: 20px;
                    color: rgba(184, 149, 45, 1);
                    font-size: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .first-time-actions {
                    margin-bottom: 24px;
                }

                .first-time-actions .btn {
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 1.05rem;
                    padding: 16px 24px;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                }

                .first-time-actions .btn-golden {
                    background: linear-gradient(135deg, rgba(184, 149, 45, 0.9), rgba(184, 149, 45, 0.7));
                    border: 1px solid rgba(184, 149, 45, 0.6);
                    color: white;
                }

                .first-time-actions .btn-golden:hover {
                    background: linear-gradient(135deg, rgba(184, 149, 45, 1), rgba(184, 149, 45, 0.8));
                    border-color: rgba(184, 149, 45, 0.8);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(184, 149, 45, 0.4);
                }

                .first-time-actions .btn-outline-light {
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: rgba(255, 255, 255, 0.9);
                }

                .first-time-actions .btn-outline-light:hover {
                    background: rgba(255, 255, 255, 0.15);
                    border-color: rgba(255, 255, 255, 0.3);
                    color: white;
                    transform: translateY(-1px);
                }

                .first-time-footer {
                    text-align: center;
                }

                .first-time-footer small {
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 0.9rem;
                }

                /* Mobile responsiveness */
                @media (max-width: 767px) {
                    .first-time-modal-content {
                        width: 95%;
                        max-width: none;
                        margin: 20px;
                    }
                    
                    .first-time-container {
                        padding: 50px 32px 40px;
                    }
                    
                    .first-time-header h2 {
                        font-size: 1.8rem;
                    }
                    
                    .benefit-item {
                        font-size: 1rem;
                    }
                }
            </style>
        `;
        
        document.head.insertAdjacentHTML('beforeend', styles);
    }

    /**
     * Set up event listeners for the prompt modal
     */
    setupEventListeners() {
        // Remove any existing listeners to prevent duplicates
        if (this.clickHandler) {
            document.removeEventListener('click', this.clickHandler);
        }
        if (this.keyHandler) {
            document.removeEventListener('keydown', this.keyHandler);
        }

        // Create bound handlers to maintain 'this' context
        this.clickHandler = (e) => {
            // Handle close button click (including child elements like the icon)
            if (e.target.id === 'close-first-time-prompt' || 
                e.target.closest('#close-first-time-prompt')) {
                e.preventDefault();
                e.stopPropagation();
                console.log('FirstTimeUserPrompt: Close button clicked');
                this.closePrompt();
                return;
            }
            
            // Handle continue button click
            if (e.target.id === 'first-time-continue-btn') {
                e.preventDefault();
                e.stopPropagation();
                this.continueWithoutSignIn();
                return;
            }
            
            // Handle backdrop click (clicking outside modal content)
            if (e.target.id === 'first-time-prompt-modal') {
                console.log('FirstTimeUserPrompt: Backdrop clicked');
                this.closePrompt();
                return;
            }
        };

        this.keyHandler = (e) => {
            if (e.key === 'Escape') {
                const modal = document.getElementById('first-time-prompt-modal');
                if (modal && modal.style.display !== 'none') {
                    console.log('FirstTimeUserPrompt: Escape key pressed');
                    this.closePrompt();
                }
            }
        };

        // Add event listeners
        document.addEventListener('click', this.clickHandler);
        document.addEventListener('keydown', this.keyHandler);
        
        console.log('FirstTimeUserPrompt: Event listeners set up successfully');
    }

    /**
     * Show the login prompt for unauthenticated users
     * This should be called when a user asks a question
     */
    showPrompt() {
        // Don't show if user is authenticated or already shown this session
        if (this.isUserAuthenticated || this.hasShownPromptThisSession) {
            return false;
        }

        let modal = document.getElementById('first-time-prompt-modal');
        
        // Create modal if it doesn't exist
        if (!modal) {
            this.createPromptModal();
            this.setupEventListeners();
            modal = document.getElementById('first-time-prompt-modal');
        }
        
        if (modal) {
            modal.style.display = 'flex';
            this.hasShownPromptThisSession = true; // Mark as shown for this session only
            console.log('FirstTimeUserPrompt: Showing login benefits prompt for unauthenticated user');
            return true;
        }
        
        return false;
    }

    /**
     * Close the prompt modal with smooth animation
     */
    closePrompt() {
        console.log('FirstTimeUserPrompt: closePrompt called');
        const modal = document.getElementById('first-time-prompt-modal');
        const modalContent = modal?.querySelector('.first-time-modal-content');
        
        if (modal && modalContent) {
            console.log('FirstTimeUserPrompt: Closing modal with animation');
            // Apply closing animation
            modalContent.style.animation = 'firstTimeSlideOut 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards';
            
            // Hide modal after animation completes
            setTimeout(() => {
                modal.style.display = 'none';
                // Reset animation for next time
                modalContent.style.animation = '';
                this.markUserAsExperienced();
                
                console.log('FirstTimeUserPrompt: Modal closed successfully');
                
                // Process any pending message after popup is handled
                this.onPromptClosed();
            }, 400);
        } else {
            console.warn('FirstTimeUserPrompt: Modal or modal content not found for closing');
        }
    }

    /**
     * Handle actions after prompt is closed
     */
    onPromptClosed() {
        // Notify ChatController to process pending message
        if (window.chatController && typeof window.chatController.processPendingMessage === 'function') {
            console.log('FirstTimeUserPrompt: Triggering pending message processing');
            window.chatController.processPendingMessage();
        }
    }

    /**
     * Mark user as having seen the prompt this session
     */
    markUserAsExperienced() {
        this.hasShownPromptThisSession = true;
        console.log('FirstTimeUserPrompt: User marked as having seen prompt this session');
    }

    /**
     * Open the authentication modal
     */
    openAuthModal() {
        console.log('FirstTimeUserPrompt: openAuthModal called');
        this.closePrompt();
        
        // Delay to allow closing animation to complete before showing auth modal
        setTimeout(() => {
            this.forceShowAuthModal();
        }, 450);
    }

    /**
     * Force show the authentication modal with all fallback methods
     */
    forceShowAuthModal() {
        console.log('FirstTimeUserPrompt: forceShowAuthModal called');
        
        // Method 1: Use the global showAuthModal function if available
        if (typeof window.showAuthModal === 'function') {
            console.log('FirstTimeUserPrompt: Using global showAuthModal function');
            try {
                window.showAuthModal();
                return;
            } catch (error) {
                console.error('FirstTimeUserPrompt: Error calling showAuthModal:', error);
            }
        }
        
        // Method 2: Directly manipulate the auth modal
        const authModal = document.getElementById('auth-modal');
        console.log('FirstTimeUserPrompt: Auth modal found:', !!authModal);
        
        if (authModal) {
            // Force show the modal with all necessary steps
            authModal.style.display = 'flex';
            authModal.style.visibility = 'visible';
            authModal.style.opacity = '1';
            authModal.style.zIndex = '10000';
            
            // Reset and show signin form
            const signinForm = document.getElementById('signin-form');
            const emailForm = document.getElementById('email-form');
            const authLoading = document.getElementById('auth-loading');
            const authError = document.getElementById('auth-error');
            const authUserProfile = document.getElementById('auth-user-profile');
            
            // Hide all other forms
            [emailForm, authLoading, authError, authUserProfile].forEach(element => {
                if (element) element.style.display = 'none';
            });
            
            // Show signin form
            if (signinForm) {
                signinForm.style.display = 'block';
                signinForm.style.visibility = 'visible';
                console.log('FirstTimeUserPrompt: Signin form forced to display');
            }
            
            // Ensure modal content is visible
            const modalContent = authModal.querySelector('.auth-modal-content');
            if (modalContent) {
                modalContent.style.display = 'block';
                modalContent.style.visibility = 'visible';
            }
            
            console.log('FirstTimeUserPrompt: Auth modal forced to display');
            return;
        }
        
        // Method 3: Try clicking an existing auth button
        const authButtons = document.querySelectorAll('[id*="auth"], [class*="auth-button"], .auth-button');
        for (const button of authButtons) {
            if (button.style.display !== 'none' && button.offsetParent !== null) {
                console.log('FirstTimeUserPrompt: Clicking existing auth button:', button);
                button.click();
                return;
            }
        }
        
        // Method 4: Dispatch a custom event as last resort
        console.log('FirstTimeUserPrompt: Dispatching custom auth event');
        document.dispatchEvent(new CustomEvent('openAuthModal', {
            detail: { source: 'FirstTimeUserPrompt' }
        }));
        
        // Method 5: Try to find and trigger settings/profile button
        const settingsButton = document.querySelector('[id*="settings"], .settings-btn, #profile-btn');
        if (settingsButton) {
            console.log('FirstTimeUserPrompt: Trying settings button');
            settingsButton.click();
        }
    }

    /**
     * Continue without signing in
     */
    continueWithoutSignIn() {
        this.closePrompt();
        console.log('FirstTimeUserPrompt: User chose to continue without signing in for this session');
    }

    /**
     * Check if should show prompt (public method for external checking)
     */
    shouldShowPrompt() {
        console.log('FirstTimeUserPrompt: Checking if popup should show...');
        console.log('  - isUserAuthenticated:', this.isUserAuthenticated);
        console.log('  - hasShownPromptThisSession:', this.hasShownPromptThisSession);
        console.log('  - Firebase currentUser:', window.firebaseAuth?.currentUser || 'not available');
        
        // Double-check authentication status in real-time using Firebase auth
        if (window.firebaseAuth && window.firebaseAuth.currentUser) {
            this.isUserAuthenticated = true;
            console.log('FirstTimeUserPrompt: User authenticated via Firebase - popup disabled');
            return false;
        }
        
        // Don't show if already shown this session
        if (this.hasShownPromptThisSession) {
            console.log('FirstTimeUserPrompt: Already shown this session - popup disabled');
            return false;
        }
        
        // Show for unauthenticated users once per session
        const shouldShow = !this.isUserAuthenticated;
        console.log('FirstTimeUserPrompt: Final decision - popup should show:', shouldShow);
        return shouldShow;
    }

    /**
     * Reset the session status (for testing purposes)
     */
    resetSessionStatus() {
        this.hasShownPromptThisSession = false;
        console.log('FirstTimeUserPrompt: Reset session status - prompt will show again this session');
    }

    /**
     * Force refresh of authentication status
     */
    refreshAuthenticationStatus() {
        if (window.firebaseAuth && window.firebaseAuth.currentUser) {
            this.isUserAuthenticated = true;
            console.log('FirstTimeUserPrompt: Authentication status refreshed - user is authenticated');
        } else {
            this.isUserAuthenticated = false;
            console.log('FirstTimeUserPrompt: Authentication status refreshed - user is not authenticated');
        }
    }

    /**
     * Force show popup for testing (ignores session and auth status)
     */
    forceShowPopup() {
        console.log('FirstTimeUserPrompt: Force showing popup for testing');
        this.createPromptModal();
        this.setupEventListeners();
        const modal = document.getElementById('first-time-prompt-modal');
        if (modal) {
            modal.style.display = 'flex';
            console.log('FirstTimeUserPrompt: Test popup displayed');
        } else {
            console.error('FirstTimeUserPrompt: Could not find modal element');
        }
    }
}

// Global function to handle first-time sign in button click
window.firstTimeSignIn = function() {
    // Use the smooth closing animation
    const modal = document.getElementById('first-time-prompt-modal');
    const modalContent = modal?.querySelector('.first-time-modal-content');
    
    if (modal && modalContent) {
        // Apply closing animation
        modalContent.style.animation = 'firstTimeSlideOut 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards';
        
        // Hide modal and show auth modal after animation completes
        setTimeout(() => {
            modal.style.display = 'none';
            modalContent.style.animation = '';
            
            // Process pending message after authentication flow starts
            if (window.chatController && typeof window.chatController.processPendingMessage === 'function') {
                console.log('Processing pending message after sign-in initiated');
                window.chatController.processPendingMessage();
            }
            
            // Open the auth modal using the existing global function
            if (typeof window.showAuthModal === 'function') {
                window.showAuthModal();
            } else {
                console.error('showAuthModal function not available');
            }
        }, 400);
    }
};